import type { Recipe } from "@/types/recipe";
import type { MenuItem } from "@/types/menu-item";
import type { I18n } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import type { SubIngredientRow } from "../shared-types";
import { toSubIngredientRows } from "../ingredient-rows";
import type { MenuItemRow } from "./types";
import { getMenuItemColumns } from "./columns";

function mapRecipeStatus(recipe: Recipe): MenuItemRow["status"] {
  if (!recipe.isActive) return "Archived";
  return recipe.isPublished ? "Published" : "Draft";
}

function mapRecipeTargets(recipe: Recipe, i18n: I18n): string {
  const hasRegionScope =
    recipe.scopes?.some((scope) => scope.scopeType === "REGION" && !scope.isExcluded) ?? false;

  return hasRegionScope ? i18n._(msg`Region share`) : i18n._(msg`Org-wide`);
}

/** Maps API Recipe → MenuItemRow for table display */
export function toMenuItemRows(recipes: Recipe[], i18n: I18n): MenuItemRow[] {
  return recipes.map((r) => {
    const totalMinutes = (r.prepTimeMinutes ?? 0) + (r.cookTimeMinutes ?? 0);
    const allergenCodes = (r.allergens ?? []).map((a) => a.code);
    return {
      id: r.id,
      name: r.name,
      recipeCode: r.slug,
      description: r.description ?? undefined,
      status: mapRecipeStatus(r),
      prepTime: totalMinutes > 0 ? i18n._(msg`${totalMinutes} min`) : undefined,
      unitCost: r.targetSellingPrice ?? null,
      targets: mapRecipeTargets(r, i18n),
      allergens: allergenCodes.length > 0 ? allergenCodes.join(", ") : undefined,
      station: r.subcategory ?? undefined,
      category: r.category ?? undefined,
      ingredients: toSubIngredientRows(r),
    };
  });
}

/** Maps a member recipe of a menu item into the expandable sub-row shape. */
function mapMemberRecipes(item: MenuItem, i18n: I18n): SubIngredientRow[] {
  return (item.recipes ?? []).map((member) => ({
    id: member.recipeId,
    name: member.recipe?.name ?? i18n._(msg`Recipe #${member.recipeId}`),
    quantity: member.quantity,
    unit: "x",
    isSubRecipe: true,
  }));
}

/** Maps an API MenuItem (a group of recipes) → MenuItemRow for table display. */
export function menuItemsToRows(items: MenuItem[], i18n: I18n): MenuItemRow[] {
  return items.map((item) => {
    const price = item.price != null ? Number(item.price) : null;
    const count = item.recipes?.length ?? 0;
    return {
      id: item.id,
      name: item.name,
      description: item.description ?? undefined,
      imageUrl: item.recipes?.[0]?.recipe?.imageUrl ?? undefined,
      status: item.isAvailable ? "Published" : "Draft",
      unitCost: price != null && !Number.isNaN(price) ? price : null,
      costPerPortion: item.costPerPortion ?? null,
      costIsComplete: item.costIsComplete ?? null,
      station: item.station ?? undefined,
      category: item.menuCategory?.name ?? item.category ?? undefined,
      targets: i18n._(msg`${plural(count, { one: "# recipe", other: "# recipes" })}`),
      ingredients: mapMemberRecipes(item, i18n),
    };
  });
}

export const menuItemListColumns = (i18n: I18n) => getMenuItemColumns(i18n);
export const menuItemPanelColumns = (i18n: I18n) => getMenuItemColumns(i18n, { expandable: true });
